'use client';

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectItem,
  SelectContent,
  RadioGroup,
  RadioGroupItem,
  Label,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  Button,
  Field,
  FieldLabel,
  FieldError,
  FieldContent,
} from '@shared/shared-shadcn-ui';
import { FormInput } from '../form-input/form-input';
import { cn } from '@shared/shared-utils';
import {
  AppLink,
  LazyRecaptcha,
} from '../../../../../libs/components/components';
import { FormButton } from '../../../../_libs/components/components';
import { AppRoute } from '../../../../../libs/enums/enums';
import { getUpdateFormValidationSchema } from '../../validation-schemas/validation-schemas';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GetUpdateFormType } from '../../types/types';
import { FORM_STATES, WOUND_LOCATIONS, USER_TYPES } from '../../constants';
import { useMutation } from 'react-query';
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  getCookie,
  setFormRegistrationCookie,
} from '../../../../../libs/packages/hubspotutk';
import ArrowRightIcon from '../../../../../../public/svg/arrow-right.svg';
import {
  sendFormFieldInteractionEvent,
  sendFormSubmitEvent,
} from '@shared/shared-gtm-tracker';

const formId = 'get-updates-form-registration';

const GetUpdatesForm = () => {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    reset,
    clearErrors,
  } = useForm<GetUpdateFormType>({
    resolver: zodResolver(getUpdateFormValidationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      userType: USER_TYPES[0].value,
      woundLocation: [],
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      termsAgree: false,
    },
    mode: 'onChange',
  });

  const { mutate: submitForm } = useMutation({
    mutationFn: async (data: GetUpdateFormType) => {
      const formData = {
        fields: [
          {
            objectTypeId: '0-1',
            name: 'email',
            value: data.email,
          },
          {
            objectTypeId: '0-1',
            name: 'firstname',
            value: data.firstName,
          },
          {
            objectTypeId: '0-1',
            name: 'lastname',
            value: data.lastName,
          },
          {
            objectTypeId: '0-1',
            name: 'user_type',
            value: data.userType,
          },
          {
            objectTypeId: '0-1',
            name: 'wound_location',
            value: Array.isArray(data.woundLocation)
              ? data.woundLocation.join(', ')
              : String(data.woundLocation || ''),
          },
          {
            objectTypeId: '0-1',
            name: 'address',
            value: data.address,
          },
          {
            objectTypeId: '0-1',
            name: 'apt',
            value: data.address2,
          },
          {
            objectTypeId: '0-1',
            name: 'city',
            value: data.city,
          },
          {
            objectTypeId: '0-1',
            name: 'state',
            value: data.state,
          },
          {
            objectTypeId: '0-1',
            name: 'zip',
            value: data.zip,
          },
        ],
        context: {
          hutk: getCookie('hubspotutk'),
          pageUri: window.location.href,
          pageName: document.title,
        },
        legalConsentOptions: {
          consent: {
            consentToProcess: true,
            text: 'I authorize Organogenesis and those working with or on behalf of Organogenesis to contact me via email, mail, or phone to conduct marketing activities.',
            communications: [
              {
                value: true,
                subscriptionTypeId: 84016497, // Production subscription type ID
                text: 'I agree to receive marketing communications from Organogenesis and those working with or on behalf of Organogenesis.',
              },
            ],
          },
        },
      };

      const hostname = window.location.hostname;
      let hubspotConfig;

      switch (hostname) {
        case 'localhost':
          hubspotConfig = {
            accessToken: 'pat-eu1-18f4f8c6-c0b5-43b4-813c-b2e1f0bc20a6',
            portalId: '139584591',
            formId: 'a24005ab-d9ca-437a-8b21-d82b86bdc2ca',
            subscriptionTypeId: 182897238,
          };
          break;
        default:
          hubspotConfig = {
            accessToken: 'pat-na1-9d7fc4b5-f42f-4d2a-aa16-8d4a95bb34d6',
            portalId: '24433678',
            formId: 'd4d9ebc7-ed71-41b7-8319-6b6897f7893d',
            subscriptionTypeId: 84016497,
          };
          break;
      }

      formData.legalConsentOptions.consent.communications[0].subscriptionTypeId =
        hubspotConfig.subscriptionTypeId;

      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotConfig.portalId}/${hubspotConfig.formId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${hubspotConfig.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      return response.json();
    },
    onSuccess: () => {
      setFormRegistrationCookie();
      setShowSuccessDialog(true);
    },
    onError: () => {
      setShowErrorDialog(true);
    },
  });

  const onSubmit = async (data: GetUpdateFormType) => {
    try {
      sendFormSubmitEvent(formId, data);

      submitForm(data);
    } catch (error) {
      console.error('Form submission error:', error);
      // Handle submission error
    }
  };

  return (
    <div className="bg-white text-ogn-blue rounded-[10px] pt-[32px] lg:pl-[50px] lg:pr-[42px] max-w-[1180px]">
      <h1 className="text-[60px] lg:text-[65px] font-roboto-condensed font-bold">
        Get Updates
      </h1>
      <h2 className="mt-6 text-[36px] leading-[40px] lg:text-[38px] text-[#3D4543] font-roboto-condensed">
        Don’t wait another moment. Start your healing journey now.
      </h2>
      <p className="text-[16px] lg:text-[18px] mt-3 text-[#3D4543]">
        Please fill out the form below to receive valuable resources and future
        communication from Organogenesis.
      </p>
      <div className="flex justify-end mt-5">
        <span className="text-[16px] text-black font-bold mr-12">
          *Required
        </span>
      </div>

      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="mt-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4 lg:gap-y-11">
          {/* First name */}
          <Controller
            control={control}
            name="firstName"
            render={({ field }) => (
              <Field data-invalid={!!errors.firstName}>
                <FieldContent className="gap-2">
                  <FormInput
                    placeholder="First name*"
                    {...field}
                    className={cn(
                      errors.firstName && 'border-red-500 focus:border-red-500',
                    )}
                  />
                  {errors.firstName && (
                    <FieldError
                      className="text-red-500 text-sm font-medium"
                      errors={[errors.firstName]}
                    />
                  )}
                </FieldContent>
              </Field>
            )}
          />

          {/* Last name */}
          <Controller
            control={control}
            name="lastName"
            render={({ field }) => (
              <Field data-invalid={!!errors.lastName}>
                <FieldContent className="gap-2">
                  <FormInput
                    placeholder="Last name*"
                    {...field}
                    className={cn(
                      errors.lastName && 'border-red-500 focus:border-red-500',
                    )}
                  />
                  {errors.lastName && (
                    <FieldError
                      className="text-red-500 text-sm font-medium"
                      errors={[errors.lastName]}
                    />
                  )}
                </FieldContent>
              </Field>
            )}
          />

          {/* Email */}
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={!!errors.email}>
                <FieldContent className="gap-2">
                  <FormInput
                    placeholder="Email*"
                    type="email"
                    {...field}
                    className={cn(
                      errors.email && 'border-red-500 focus:border-red-500',
                    )}
                  />
                  {errors.email && (
                    <FieldError
                      className="text-red-500 text-sm font-medium"
                      errors={[errors.email]}
                    />
                  )}
                </FieldContent>
              </Field>
            )}
          />

          {/* User Type */}
          <Controller
            control={control}
            name="userType"
            render={({ field }) => (
              <Field data-invalid={!!errors.userType}>
                <FieldContent className="gap-2">
                  <Select
                    onValueChange={(val) => {
                      field.onChange(val);
                      const syntheticEvent = {
                        target: {
                          name: 'userType',
                          value: val,
                          form: document.getElementById(
                            formId,
                          ) as HTMLFormElement,
                        },
                      } as unknown as ChangeEvent<HTMLSelectElement>;
                      sendFormFieldInteractionEvent(syntheticEvent);
                    }}
                    value={field.value}
                  >
                    <SelectTrigger
                      aria-label="Select user type"
                      className={cn(
                        'w-full lg:w-2/3 lg:pl-[30px] border-[rgba(0,_0,_0,0.12)] rounded-[6px] py-7 text-[#1C1C1C] text-[20px] leading-[23px] font-bold',
                        errors.userType && 'border-red-500',
                      )}
                    >
                      <SelectValue placeholder="Select user type*" />
                    </SelectTrigger>
                    <SelectContent
                      withPortal={false}
                      className="rounded-b-[6px] !p-0 z-10 -top-1 bg-white"
                    >
                      {USER_TYPES.map((item) => (
                        <SelectItem
                          className="py-5 m-0 text-[#1C1C1C] text-[18px] lg:text-[20px] font-bold cursor-pointer hover:bg-ogn-blue hover:text-white data-[state=checked]:bg-ogn-blue data-[state=checked]:text-white"
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.userType && (
                    <FieldError
                      className="text-red-500 text-sm font-medium"
                      errors={[errors.userType]}
                    />
                  )}
                </FieldContent>
              </Field>
            )}
          />

          {/* Wound Location (Multiple) */}
          <div className="lg:col-span-2">
            <Controller
              control={control}
              name="woundLocation"
              render={({ field }) => (
                <Field data-invalid={!!errors.woundLocation}>
                  <FieldContent className="space-y-4">
                    <FieldLabel className="text-[#3D4543] text-[20px] font-bold">
                      Where is your wound located?*
                    </FieldLabel>
                    <div className="flex flex-col lg:flex-row gap-x-16 gap-y-8 flex-wrap w-full">
                      {WOUND_LOCATIONS.map((item) => {
                        const handleToggle = () => {
                          const current = Array.isArray(field.value)
                            ? field.value
                            : [];
                          const isSelected = current.includes(item.value);
                          const next = isSelected
                            ? current.filter((v: string) => v !== item.value)
                            : [...current, item.value];
                          setValue('woundLocation', next, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          const syntheticEvent = {
                            target: {
                              name: 'woundLocation',
                              value: Array.isArray(next)
                                ? next.join(', ')
                                : String(next || ''),
                              form: document.getElementById(
                                formId,
                              ) as HTMLFormElement,
                            },
                          } as unknown as ChangeEvent<HTMLSelectElement>;
                          sendFormFieldInteractionEvent(syntheticEvent);
                        };

                        return (
                          <div
                            key={item.value}
                            className="flex items-center gap-x-2"
                          >
                            <RadioGroup
                              value={
                                field.value.includes(item.value)
                                  ? item.value
                                  : ''
                              }
                              onValueChange={() => {
                                return;
                              }}
                            >
                              <RadioGroupItem
                                className="w-[24px] h-[24px] mt-[2px] [&>span>svg]:data-[state=checked]:w-[16px] [&>span>svg]:data-[state=checked]:h-[16px]"
                                value={item.value}
                                id={`wound-location-${item.value}`}
                                onClick={handleToggle}
                              />
                            </RadioGroup>
                            <Label
                              htmlFor={`wound-location-${item.value}`}
                              className="text-[#3D4543] text-[18px] font-bold cursor-pointer"
                              onClick={handleToggle}
                            >
                              {item.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                    {errors.woundLocation && (
                      <FieldError
                        className="text-red-500 text-sm font-medium"
                        errors={[errors.woundLocation]}
                      />
                    )}
                  </FieldContent>
                </Field>
              )}
            />
          </div>

          {/* Address */}
          <div className="flex-col lg:flex-row lg:col-span-2 flex gap-4">
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <Field data-invalid={!!errors.address} className="w-full">
                  <FieldContent className="gap-2">
                    <FormInput
                      placeholder="Street address"
                      {...field}
                      className={cn(
                        'w-full',
                        errors.address && 'border-red-500 focus:border-red-500',
                      )}
                    />
                    {errors.address && (
                      <FieldError
                        className="text-red-500 text-sm font-medium"
                        errors={[errors.address]}
                      />
                    )}
                  </FieldContent>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="address2"
              render={({ field }) => (
                <Field>
                  <FieldContent className="gap-2">
                    <FormInput
                      placeholder="Apt"
                      {...field}
                      className="w-full max-w-[170px]"
                    />
                  </FieldContent>
                </Field>
              )}
            />
          </div>

          {/* City, State, ZIP */}
          <div className="flex-col lg:flex-row lg:col-span-2 flex gap-4">
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <Field
                  data-invalid={!!errors.city}
                  className="w-full max-w-[535px]"
                >
                  <FieldContent className="gap-2">
                    <FormInput
                      placeholder="City"
                      {...field}
                      className={cn(
                        errors.city && 'border-red-500 focus:border-red-500',
                      )}
                    />
                    {errors.city && (
                      <FieldError
                        className="text-red-500 text-sm font-medium"
                        errors={[errors.city]}
                      />
                    )}
                  </FieldContent>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="state"
              render={({ field }) => (
                <Field data-invalid={!!errors.state}>
                  <FieldContent className="gap-2">
                    <Select
                      onValueChange={(val) => {
                        field.onChange(val);
                        const syntheticEvent = {
                          target: {
                            name: field.name,
                            value: val,
                            form: document.getElementById(
                              formId,
                            ) as HTMLFormElement,
                          },
                        } as unknown as ChangeEvent<HTMLSelectElement>;
                        sendFormFieldInteractionEvent(syntheticEvent);
                      }}
                      value={field.value}
                    >
                      <SelectTrigger
                        aria-label="Select state"
                        className={cn(
                          'w-auto lg:pl-[20px] border-[rgba(0,_0,_0,0.12)] rounded-[6px] py-7 text-[#1C1C1C] data-[placeholder]:text-[#1C1C1C] text-[18px] lg:text-[20px] leading-[23px] font-bold',
                          errors.state && 'border-red-500',
                        )}
                      >
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent
                        withPortal={false}
                        className="rounded-b-[6px] !p-0 z-10 bg-white"
                      >
                        {FORM_STATES.map((state) => (
                          <SelectItem
                            className="py-5 m-0 text-[18px] lg:text-[20px] font-bold cursor-pointer hover:bg-ogn-blue hover:text-white data-[state=checked]:bg-ogn-blue data-[state=checked]:text-white"
                            key={state}
                            value={state}
                          >
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.state && (
                      <FieldError
                        className="text-red-500 text-sm font-medium"
                        errors={[errors.state]}
                      />
                    )}
                  </FieldContent>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="zip"
              render={({ field }) => (
                <Field data-invalid={!!errors.zip}>
                  <FieldContent className="gap-2">
                    <FormInput
                      maxLength={5}
                      placeholder="ZIP code"
                      {...field}
                      className={cn(
                        'w-full lg:max-w-[140px] min-w-[105px] lg:text-center',
                        errors.zip && 'border-red-500 focus:border-red-500',
                      )}
                    />
                    {errors.zip && (
                      <FieldError
                        className="text-red-500 text-sm font-medium"
                        errors={[errors.zip]}
                      />
                    )}
                  </FieldContent>
                </Field>
              )}
            />
          </div>

          {/* Marketing Consent */}
          <div className="lg:col-span-2">
            <Controller
              control={control}
              name="termsAgree"
              render={({ field }) => (
                <Field data-invalid={!!errors.termsAgree}>
                  <div className="flex gap-x-4">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        const syntheticEvent = {
                          target: {
                            name: field.name,
                            value: checked ? 'on' : 'off',
                            form: document.getElementById(
                              formId,
                            ) as HTMLFormElement,
                          },
                        } as unknown as ChangeEvent<HTMLInputElement>;
                        sendFormFieldInteractionEvent(syntheticEvent);
                      }}
                      id="marketing-consent"
                      className={cn(
                        'w-[22px] h-[22px] lg:w-[42px] lg:h-[42px] text-black border-[rgba(0,_0,_0,0.4)] rounded-[6px] [&>span>svg]:w-4 [&>span>svg]:h-4 lg:[&>span>svg]:w-6 lg:[&>span>svg]:h-6 [&>span>svg]:text-black data-[state=checked]:[&>span>svg]:text-black',
                        errors.termsAgree && 'border-red-500',
                      )}
                    />
                    <FieldContent className="flex flex-col gap-y-10">
                      <FieldLabel
                        htmlFor="marketing-consent"
                        className="text-black text-[16px] lg:text-[18px] font-bold cursor-pointer flex flex-col"
                      >
                        I authorize Organogenesis and those working with or on
                        behalf of Organogenesis to contact me via email, mail,
                        or phone to conduct marketing activities, which
                        includes, but is not limited to, providing me with
                        educational and promotional materials, information on
                        Organogenesis products and patient support services
                        related to my treatment or health condition and/or to
                        conduct market research activities, which includes
                        contacting me to participate in various activities such
                        as surveys, focus groups, or interviews which would be
                        funded by Organogenesis. I understand that Organogenesis
                        will not release my personally identifiable information
                        to any party, except as provided in this authorization
                        or as permitted by applicable law, without first
                        obtaining my (or my authorized representatives) separate
                        written consent. I understand that I may revoke this
                        authorization at any time by contacting Organogenesis at
                        +1 888 432 5232 or in writing at Organogenesis, 85 Dan
                        Road, Canton, MA 02021. Organogenesis will not sell,
                        rent, or otherwise distribute your name or any
                        personally identifiable information outside of
                        Organogenesis and its agents. Organogenesis will only
                        use your information in accordance with our{' '}
                        <AppLink
                          to="https://www.organogenesis.com/privacy.html"
                          className="text-[#007297] cursor-pointer fancy-underline w-fit"
                          target="_blank"
                        >
                          Privacy Policy.
                        </AppLink>
                      </FieldLabel>
                      <div>
                        <AppLink
                          to="https://www.organogenesis.com/privacy.html#ca-privacy-rights"
                          className="text-[#007297] cursor-pointer font-bold fancy-underline"
                          target="_blank"
                        >
                          Your Privacy Rights as a California Resident
                        </AppLink>
                      </div>
                      {errors.termsAgree && (
                        <FieldError
                          className="text-red-500 text-sm font-medium"
                          errors={[errors.termsAgree]}
                        />
                      )}
                    </FieldContent>
                  </div>
                </Field>
              )}
            />
          </div>
        </div>

        <div className="flex justify-center lg:justify-between gap-x-4 flex-wrap gap-y-8 items-center mt-12">
          <LazyRecaptcha
            siteKey={String(process.env.NEXT_PUBLIC_CAPCHA_SITE_KEY)}
            onChange={(value) => {
              setRecaptchaValue(value || '');
            }}
          />
          <FormButton
            onClick={handleSubmit(onSubmit)}
            className={cn('lg:ml-auto w-[300px] lg:text-[32px] lg:mr-12', {
              'cursor-not-allowed': isSubmitting || !isValid,
            })}
            isDisabled={isSubmitting || !isValid || !recaptchaValue}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </FormButton>
          {/* Success Dialog */}
          <Dialog
            onOpenChange={(open) => {
              setShowSuccessDialog(open);
              if (!open) {
                reset();
                clearErrors();
              }
            }}
            open={showSuccessDialog}
          >
            <DialogContent className="bg-white h-[545px] max-w-[950px]">
              <DialogTitle className="sr-only">
                Registration successful
              </DialogTitle>
              <DialogDescription className="sr-only">
                Your registration was submitted successfully. You can now view
                resources.
              </DialogDescription>
              <div className="h-full flex flex-col justify-between">
                <div>
                  <h2 className="text-[65px] text-center font-roboto-condensed text-ogn-blue font-bold">
                    Thank you for signing up.
                  </h2>
                  <p className="text-[20px] text-center text-[#3D4543] font-bold max-w-[590px] mx-auto">
                    Congratulations on taking a step forward on your healing
                    journey. To access more valuable resources, click below.
                  </p>
                </div>
                <Button
                  asChild
                  className="flex items-center justify-center h-auto bg-ogn-blue hover:bg-ogn-blue/90 text-[32px] font-bold text-ogn-white py-2 leading-normal font-roboto rounded-[34px] mx-auto w-[300px] mb-[137px] gap-x-2"
                >
                  <AppLink
                    to={AppRoute.RESOURCES_SUPPORT}
                    className="flex items-center gap-x-2"
                  >
                    View Resources
                    <ArrowRightIcon className="w-6 h-6" />
                  </AppLink>
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Error Dialog */}
          <Dialog
            onOpenChange={(open) => {
              setShowErrorDialog(open);
            }}
            open={showErrorDialog}
          >
            <DialogContent className="bg-white h-auto max-w-[650px] p-8">
              <DialogTitle className="sr-only">
                Form submission error
              </DialogTitle>
              <DialogDescription className="sr-only">
                There was an issue submitting your form. Please try again.
              </DialogDescription>
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-[32px] lg:text-[40px] font-roboto-condensed text-ogn-blue font-bold mb-4">
                    We couldn’t process your request
                  </h2>
                  <p className="text-[18px] lg:text-[20px] text-[#3D4543] font-bold max-w-[500px] mx-auto">
                    We’re sorry, but there was an issue submitting your form.
                    Please check your internet connection and try again. If the
                    problem persists, please contact us directly.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </form>
    </div>
  );
};

export { GetUpdatesForm };
